import React, { useState, useEffect } from 'react'

// Libs
import { motion } from 'framer-motion'
import { useHistory, useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { IonContent, IonPage } from '@ionic/react'
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Visibility, VisibilityOffOutlined } from '@mui/icons-material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

// Components
import HeaderSection from '../../../components/user/views/login/HeaderSection'

// Services
import FirebaseApp from '../../../services/firebaseApp'

// Helpers
import useToasterHelper from '../../../helpers/ToasterHelper'
import MessageHelperComp from '../../../helpers/MessageHelper'

// Constants
import { CONST } from '../../../const/const'

// Validations
import { resetPasswordSchema } from '../../../validations'

// Assets
import bgImage from '../../../components/user/views/login/assets/main-bg.png'

interface IResetPasswordForm {
  password: string
  confirmPassword: string
}

export const ResetPasswordPage = () => {
  const history = useHistory()
  const location = useLocation()
  const toastFunction = useToasterHelper()

  const [loading, setLoading] = useState(false)
  const [verifying, setVerifying] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [oobCode, setOobCode] = useState<string | null>(null)
  const [validCode, setValidCode] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IResetPasswordForm>({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  })

  useEffect(() => {
    // Get the action code from the URL
    const urlParams = new URLSearchParams(location.search)
    const code = urlParams.get('oobCode')
    const mode = urlParams.get('mode')

    // Verify this is a password reset action
    if (mode && mode !== 'resetPassword') {
      toastFunction.error({ message: 'Invalid action' })
      setTimeout(() => history.push(CONST.ROUTES.LOGIN.URL), 3000)
      return
    }

    const verifyCode = async (code: string) => {
      try {
        await verifyPasswordResetCode(FirebaseApp.auth, code)
        setValidCode(true)
      } catch (error: any) {
        toastFunction.error({ message: 'This reset link is invalid or has expired' })
        setTimeout(() => history.push(CONST.ROUTES.LOGIN.URL), 3000)
      } finally {
        setVerifying(false)
      }
    }

    if (code) {
      setOobCode(code)
      verifyCode(code)
    } else {
      setVerifying(false)
      toastFunction.error({ message: 'Invalid reset link' })
      setTimeout(() => history.push(CONST.ROUTES.LOGIN.URL), 3000)
    }
  }, [location, history, toastFunction])

  const onSubmit = async (data: IResetPasswordForm) => {
    if (!oobCode) return

    setLoading(true)
    try {
      await confirmPasswordReset(FirebaseApp.auth, oobCode, data.password)
      toastFunction.success({ message: 'Password reset successfully!' })
      setTimeout(() => history.push(CONST.ROUTES.LOGIN.URL), 2000)
    } catch (error: any) {
      toastFunction.error({
        message: error.message || 'Failed to reset password. Please try again.',
      })
    } finally {
      setLoading(false)
    }
  }

  if (verifying) {
    return (
      <IonPage>
        <IonContent>
          <div className="w-full h-screen flex items-center justify-center">
            <AutorenewIcon
              fontSize="large"
              className="animate-spin text-SeabiscuitMainThemeColor"
            />
          </div>
        </IonContent>
      </IonPage>
    )
  }

  if (!validCode) {
    return null
  }

  return (
    <IonPage>
      <IonContent>
        <motion.div
          className="w-full mx-auto h-full !bg-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{
            type: 'spring',
            stiffness: 100,
            damping: 20,
            delay: 0.7,
          }}
        >
          <div
            style={{
              backgroundImage: `url(${bgImage})`,
            }}
            className="h-screen flex flex-col bg-cover"
          >
            <HeaderSection />
            <div className="flex-1 hide-scroll-bar overflow-auto pb-10 min-h-[calc(100vh_-_118px)] text-white flex items-center">
              <div className="md:w-[470px] sm:w-[350px] w-[300px] mx-auto md:ml-[100px] transition-[all_.3s]">
                <div>
                  <h1 className="text-[22px] font-normal text-white mt-4">Reset Password</h1>
                  <p className="text-white/80 text-sm mt-2 mb-6">Enter your new password below</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-3 text-white flex items-center gap-2 w-full px-4 py-2 border-solid rounded-xl border-white border-[1px] bg-transparent">
                        <div>
                          <img src="/assets/og_icons/Lock-4.svg" alt="icon" />
                        </div>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          {...register('password')}
                          placeholder="New Password"
                          className="flex-1 border-0 outline-0 !text-white bg-transparent placeholder:text-white focus:ring-transparent w-full focus:border-none"
                        />
                        <div
                          className="cursor-pointer"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <Visibility className="text-white" />
                          ) : (
                            <VisibilityOffOutlined className="text-white" />
                          )}
                        </div>
                      </label>
                      {errors.password && (
                        <MessageHelperComp
                          className="mb-3 !mt-0 bg-white rounded-lg px-2 opacity-80 w-fit"
                          isError={true}
                          message={errors.password.message}
                        />
                      )}
                    </div>

                    <div>
                      <label className="mb-3 text-white flex items-center gap-2 w-full px-4 py-2 border-solid rounded-xl border-white border-[1px] bg-transparent">
                        <div>
                          <img src="/assets/og_icons/Lock-4.svg" alt="icon" />
                        </div>
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...register('confirmPassword')}
                          placeholder="Confirm Password"
                          className="flex-1 border-0 outline-0 !text-white bg-transparent placeholder:text-white focus:ring-transparent w-full focus:border-none"
                        />
                        <div
                          className="cursor-pointer"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? (
                            <Visibility className="text-white" />
                          ) : (
                            <VisibilityOffOutlined className="text-white" />
                          )}
                        </div>
                      </label>
                      {errors.confirmPassword && (
                        <MessageHelperComp
                          className="mb-3 !mt-0 bg-white rounded-lg px-2 opacity-80 w-fit"
                          isError={true}
                          message={errors.confirmPassword.message}
                        />
                      )}
                    </div>
                  </div>

                  <div className="w-[70%] mt-[60px] mx-auto">
                    <button
                      disabled={loading}
                      type="submit"
                      className="w-full h-12 flex items-center justify-center py-2 px-4 border !border-white border-solid rounded-2xl shadow-sm text-sm font-medium text-SeabiscuitMainThemeColor nextBtnShadow bg-white opacity-80 hover:opacity-100 focus:outline-none focus:ring-0"
                    >
                      {loading ? (
                        <AutorenewIcon fontSize="small" className="animate-spin" />
                      ) : (
                        'Reset Password'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center text-white opacity-80 mt-8">
                  Remember your password?{' '}
                  <Link
                    className="text-white underline cursor-pointer hover:opacity-100"
                    to={CONST.ROUTES.LOGIN.URL}
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </IonContent>
    </IonPage>
  )
}
